import { useState, type FormEvent } from "react";

export type FormStatus = "idle" | "submitting" | "success" | "error";

function readString(source: unknown, key: string): string {
  if (source && typeof source === "object") {
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === "string") return value;
  }
  return "";
}

interface UseFormSubmitOptions {
  submitAddr: string;
  honeypotName: string;
  successMessage: string;
}

export function useFormSubmit({ submitAddr, honeypotName, successMessage }: UseFormSubmitOptions) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get(honeypotName) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage(successMessage);
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === honeypotName) return;
      const text = typeof value === "string" ? value.trim() : value;
      if (text === "") return;
      params.append(key, String(text));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const root = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
      const meta = root.meta && typeof root.meta === "object" ? (root.meta as Record<string, unknown>) : {};
      const code = typeof root.code === "string" ? root.code : "";
      const serverMessage =
        readString(meta, "message") || readString(root, "message") || readString(meta, "detail") || responseText;

      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setMessage(successMessage);
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMessage || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return { status, message, submit };
}

export default useFormSubmit;